import { createNanoEvents } from "nanoevents"
import { Middleware, UnknownAction } from "redux"
import { StateObject } from "./types"

/**
 * Simple event bus that allows listening to Redux actions on "global level".
 */

interface Events {
    [REDUX_ACTION_EVENT]: (action: UnknownAction, state: StateObject) => void
}

const REDUX_ACTION_EVENT = "REDUX_ACTION_EVENT" as const
const reduxBus = createNanoEvents<Events>()

export const actionEmitMiddleware: Middleware<UnknownAction, StateObject> = store => next => action => {
    // Process the action first
    const result = next(action)
    // Get the changed state
    const newState = store.getState()
    // Emit the event
    reduxBus.emit(REDUX_ACTION_EVENT, action as UnknownAction, newState)
    return result
}

const activeListeners = new Map<string, () => void>()

export const listenToRedux = (key: string, handler: (action: UnknownAction, state: StateObject) => void) => {
    // If there's an active listener for this key, unsubscribe from it.
    if (activeListeners.has(key)) {
        activeListeners.get(key)!() // Execute the old unsubscribe function
    }

    const unsubscribe = reduxBus.on(REDUX_ACTION_EVENT, handler)

    // Store the unsubscribe function to be able to unsubscribe later
    activeListeners.set(key, unsubscribe)

    // Return a function to unsubscribe from the event,
    // useful for cleanup when the component unmounts (e.g. return function of useEffect hook)
    return () => {
        unsubscribe()
        activeListeners.delete(key)
    }
}
